import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Compass, Users, BookOpen, Flag, MessageCircle, Circle, MinusCircle,
  CheckCircle2, ChevronDown, ChevronUp, MailPlus, Ban, RefreshCw, Quote,
} from "lucide-react";

const DIM_ICONS = { SC: Compass, PG: Users, TP: BookOpen, CO: Flag };
const STATE_LABELS = {
  espressa: "espressa",
  condizionata: "condizionata",
  da_approfondire: "da approfondire",
  non_definito: "non definito",
};

function StateIcon({ status }) {
  if (status === "espressa") return <CheckCircle2 className="h-5 w-5 text-teal-600" />;
  if (status === "condizionata") return <Circle className="h-5 w-5 text-slate-500" />;
  if (status === "da_approfondire") return <MessageCircle className="h-5 w-5 text-amber-600" />;
  return <MinusCircle className="h-5 w-5 text-slate-400" />;
}

const AG_LABELS = {
  ag1: { confermata: "Confermata", da_definire: "Ancora da definire", cambiata: "Cambiata: sta valutando altre direzioni", colloquio: "Preferisce parlarne al colloquio" },
  ag2: { compatibile: "Al momento compatibile", da_verificare: "Da verificare con l'ITS", non_compatibile: "Al momento non compatibile", no_informazioni: "Non ha ancora le informazioni necessarie", colloquio: "Preferisce parlarne al colloquio" },
  ag3: { confermo: "Sì, la conferma", priorita_completamento: "No, oggi darebbe priorità al completamento", priorita_lavoro: "No, oggi darebbe priorità al lavoro", dipende_offerta: "Oggi dipenderebbe dalle condizioni dell'offerta", colloquio: "Preferisce parlarne al colloquio" },
};

const INVITE_STATUS_LABELS = {
  creato: "Creato", inviato: "Inviato", scaduto: "Scaduto", revocato: "Revocato",
  completato: "Completato", invio_fallito: "Invio fallito",
};

export function RottaPanel({ sess, appId, invites, onSaved }) {
  const navigate = useNavigate();
  const sb = sess?.section_b || {};
  const dims = sb.dimensions || {};
  const order = sess?.dimension_order || ["SC", "PG", "TP", "CO"];
  const meta = sess?.dimension_meta || {};
  const revisions = sess?.update_revisions || [];
  const [openBivio, setOpenBivio] = useState(null);
  const [openDim, setOpenDim] = useState(null);
  const [expiresAt, setExpiresAt] = useState("");
  const [busy, setBusy] = useState(false);

  if (!sb.dimensions) return null;
  const initialDate = (sb.submitted_at || "").slice(0, 10);
  const orToCheck = (sb.or_conditions || []).filter((o) => o.needs_check);

  const inviteAction = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const sendInvite = () => inviteAction(
    () => api.post("/admin/update-invites", { application_ids: [appId], expires_at: expiresAt || null }),
    "Invito creato e inviato");

  return (
    <Card className="institutional-card" data-testid="rotta-panel">
      <CardContent className="p-6 space-y-6">
        <div>
          <p className="eyebrow">Sezione B · Sintesi per la commissione</p>
          <h3 className="font-display text-xl font-bold text-slate-900 mt-1">La rotta ITS</h3>
          <p className="text-sm text-slate-500">Allineamento dichiarato al percorso · Risposte iniziali del {initialDate}</p>
        </div>

        <div className="text-base text-slate-800 leading-relaxed space-y-1" data-testid="rotta-narrative">
          <p>{sb.narrative?.first}</p>
          <p>{sb.narrative?.second}</p>
          {sb.narrative?.third && <p>{sb.narrative.third}</p>}
        </div>

        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {order.map((dim) => {
              const d = dims[dim] || {};
              const Icon = DIM_ICONS[dim] || Compass;
              return (
                <div key={dim} className="border border-slate-100 rounded-lg p-4" data-testid={`rotta-dim-${dim}`}>
                  <div className="flex items-center gap-2">
                    <StateIcon status={d.status} />
                    <Icon className="h-4 w-4 text-slate-400" />
                  </div>
                  <p className="mt-2 text-sm font-semibold text-slate-900">{meta[dim]?.label || dim}</p>
                  <p className="text-[11px] uppercase tracking-wide text-slate-400">Stato: {STATE_LABELS[d.status] || "—"}</p>
                  <p className="text-xs text-slate-600">{d.text}</p>
                  {(d.qualifiers || []).map((q) => (
                    <p key={q} className="text-xs text-amber-700 mt-0.5">{q}</p>
                  ))}
                  <button type="button" data-testid={`rotta-dim-toggle-${dim}`}
                    onClick={() => setOpenDim(openDim === dim ? null : dim)}
                    className="mt-2 text-xs font-medium text-[#003366] hover:underline inline-flex items-center gap-1">
                    {openDim === dim ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    Vedi le 3 risposte
                  </button>
                </div>
              );
            })}
          </div>
          {openDim && (
            <ul className="mt-3 space-y-2 border-l-2 border-slate-100 pl-4" data-testid={`rotta-dim-detail-${openDim}`}>
              {(sess.rotta_answers || []).filter((r) => r.dimension === openDim).map((r) => (
                <li key={r.item_id} className="text-xs">
                  <p className="text-slate-500"><span className="font-mono2 mr-1">{r.item_id}</span>{r.stem}</p>
                  <p className="text-slate-800 mt-0.5">{r.option_text || "Nessuna risposta disponibile"}</p>
                  <p className="text-slate-400 font-mono2 text-[10px] mt-0.5">Composizione: {r.code || "—"}</p>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <p className="text-sm font-semibold text-slate-900 mb-3">Quando la scelta incontra un bivio</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {(sb.bivio || []).map((b) => (
              <div key={b.item_id} className="border border-slate-100 rounded-lg p-4 bg-slate-50/60" data-testid={`rotta-bivio-${b.item_id}`}>
                <p className="text-sm font-semibold text-slate-900">{b.title}</p>
                <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">{b.card_text || "Nessuna risposta disponibile"}</p>
                {b.item_id === "CO1" && (
                  <p className="mt-2 border-t border-slate-200 pt-2 text-[11px] text-slate-500 leading-relaxed" data-testid="rotta-bivio-co1-note">
                    La scelta del lavoro non va interpretata come minore perseveranza o possibilità economica di rinunciarvi.
                  </p>
                )}
                {b.option_text && (
                  <>
                    <button type="button" data-testid={`rotta-bivio-toggle-${b.item_id}`}
                      onClick={() => setOpenBivio(openBivio === b.item_id ? null : b.item_id)}
                      className="mt-2 text-xs font-medium text-[#003366] hover:underline">
                      {openBivio === b.item_id ? "Nascondi la risposta" : "Leggi la risposta"}
                    </button>
                    {openBivio === b.item_id && (
                      <div className="mt-2 text-xs text-slate-500 border-t border-slate-200 pt-2" data-testid={`rotta-bivio-detail-${b.item_id}`}>
                        <p className="italic">{b.situation}</p>
                        <p className="mt-1 text-slate-700">{b.option_text}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="w-full rounded-lg border border-slate-100 bg-slate-50/60 p-4" data-testid="rotta-come-leggere">
          <p className="text-sm font-semibold text-slate-900">Come leggere la scheda</p>
          <ul className="mt-2 space-y-1 text-xs text-slate-600">
            <li className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-teal-600" /> Disponibilità espressa</li>
            <li className="flex items-center gap-2"><Circle className="h-3.5 w-3.5 shrink-0 text-slate-500" /> Condizionata: condizioni da chiarire o da contestualizzare</li>
            <li className="flex items-center gap-2"><MessageCircle className="h-3.5 w-3.5 shrink-0 text-amber-600" /> Da approfondire</li>
            <li className="flex items-center gap-2"><MinusCircle className="h-3.5 w-3.5 shrink-0 text-slate-400" /> Non definito: elementi ancora insufficienti</li>
          </ul>
          <p className="mt-2 text-[11px] text-slate-500 leading-relaxed">
            Ogni stato usa sempre testo e icona, mai il solo colore. L'ambra segnala solo aspetti da approfondire
            o condizioni da chiarire al colloquio: non è un giudizio.
          </p>
          <p className="mt-2 text-xs text-slate-500 leading-relaxed" data-testid="rotta-note">
            Sintesi delle risposte dichiarate, a supporto del colloquio. La rotta ITS è una mappa di allineamento al
            percorso: mostra l'allineamento dichiarato dal candidato alle condizioni del percorso ITS, non assegna
            punti in graduatoria e non stima la probabilità di completamento. Nessuno stato indica idoneità o inidoneità.
          </p>
        </div>

        {sb.main_question && (
          <div className="border-l-4 border-amber-400 bg-amber-50 rounded-r-lg p-4" data-testid="rotta-main-question">
            <p className="eyebrow text-amber-700">
              Il punto da chiarire al colloquio
              {sb.main_question.updated && <Badge className="ml-2 bg-amber-100 text-amber-800">Approfondimento aggiornato</Badge>}
            </p>
            <p className="mt-2 text-sm text-slate-800 italic flex gap-2">
              <Quote className="h-4 w-4 shrink-0 text-amber-500" />{sb.main_question.text}
            </p>
          </div>
        )}

        <div data-testid="rotta-or-conditions">
          <p className="text-sm font-semibold text-slate-900 mb-2">Condizioni organizzative dichiarate</p>
          <ul className="space-y-1">
            {(sb.or_conditions || []).map((o) => (
              <li key={o.id} className="text-xs flex flex-wrap gap-2" data-testid={`rotta-or-${o.id}`}>
                <span className="text-slate-500 flex-1 min-w-[220px]">{o.stem}</span>
                <span className={o.needs_check ? "font-medium text-amber-700" : "text-slate-700"}>
                  {o.option_label || "Senza risposta"}
                </span>
              </li>
            ))}
          </ul>
          {orToCheck.length > 0 && (
            <p className="mt-2 text-xs text-amber-700">
              Condizioni da verificare con l'ITS: {orToCheck.map((o) => o.id).join(", ")} — non modificano la sintesi motivazionale.
            </p>
          )}
        </div>

        <div className="border-t border-slate-100 pt-4" data-testid="rotta-updates">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <p className="text-sm font-semibold text-slate-900">Aggiornamento prima del colloquio</p>
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <Label className="text-[10px]">Scadenza (max 7 giorni, facoltativa)</Label>
                <Input data-testid="invite-expiry-input" type="date" className="h-8 text-xs w-36"
                  value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
              </div>
              <Button data-testid="invite-send-button" size="sm" variant="outline" disabled={busy} onClick={sendInvite}>
                <MailPlus className="h-4 w-4 mr-1.5" /> Invia invito aggiornamento
              </Button>
            </div>
          </div>
          {invites.length > 0 && (
            <ul className="space-y-1.5 mb-4" data-testid="invite-list">
              {invites.map((inv) => (
                <li key={inv.id} className="flex flex-wrap items-center gap-2 text-xs text-slate-600" data-testid={`invite-row-${inv.id}`}>
                  <Badge variant="secondary" className="text-[10px]">{INVITE_STATUS_LABELS[inv.status] || inv.status}</Badge>
                  <span>v{inv.update_version} · creato il {(inv.created_at || "").slice(0, 10)} · scade il {(inv.expires_at || "").slice(0, 10)}</span>
                  {["creato", "inviato", "invio_fallito"].includes(inv.status) && (
                    <>
                      <button type="button" data-testid={`invite-revoke-${inv.id}`} disabled={busy}
                        className="text-red-600 hover:underline inline-flex items-center gap-0.5"
                        onClick={() => inviteAction(() => api.post(`/admin/update-invites/${inv.id}/revoke`), "Invito revocato")}>
                        <Ban className="h-3 w-3" /> Revoca
                      </button>
                      <button type="button" data-testid={`invite-regenerate-${inv.id}`} disabled={busy}
                        className="text-[#003366] hover:underline inline-flex items-center gap-0.5"
                        onClick={() => inviteAction(() => api.post(`/admin/update-invites/${inv.id}/regenerate`), "Invito rigenerato")}>
                        <RefreshCw className="h-3 w-3" /> Rigenera
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
          {revisions.length === 0 && invites.length === 0 && (
            <p className="text-xs text-slate-500" data-testid="update-none">Aggiornamento non disponibile: nessun invito inviato.</p>
          )}
          {revisions.map((rev, i) => (
            <div key={rev.id} className="border border-slate-100 rounded-lg p-4 mb-2" data-testid={`update-revision-${rev.update_version}`}>
              <p className="text-xs font-semibold text-slate-700">
                Dichiarazioni aggiornate il {(rev.submitted_at || "").slice(0, 10)}
                {i === 0 && <Badge className="ml-2 bg-teal-100 text-teal-800">Più recente</Badge>}
              </p>
              <ul className="mt-2 space-y-1 text-xs text-slate-600">
                <li data-testid={`update-ag1-${rev.update_version}`}><span className="text-slate-400">Intenzione di frequenza:</span> {AG_LABELS.ag1[rev.answers?.ag1] || rev.answers?.ag1}</li>
                <li data-testid={`update-ag2-${rev.update_version}`}><span className="text-slate-400">Frequenza, studio e stage:</span> {AG_LABELS.ag2[rev.answers?.ag2] || rev.answers?.ag2}</li>
                <li data-testid={`update-ag3-${rev.update_version}`}>
                  <span className="text-slate-400">Scelta lavoro/percorso:</span> {AG_LABELS.ag3[rev.answers?.ag3] || rev.answers?.ag3}
                  {rev.answers?.ag3 && rev.answers.ag3 !== "confermo" && rev.answers.ag3 !== "colloquio" && (
                    <span className="ml-1 text-slate-500">— posizione aggiornata sulla scelta lavoro/percorso</span>
                  )}
                </li>
              </ul>
            </div>
          ))}
        </div>

      </CardContent>
    </Card>
  );
}
